import type { FeedListing } from "../feed-state";
import { priceNative } from "../feed-state";
import { DENSITY_STYLES, type Density } from "../density";
import { CardThumbnail } from "./CardThumbnail";
import { StatusBadge } from "./StatusBadge";

const usdFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const tokenFmt = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 4,
});

const timeFmt = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
});

const VENUE_BADGE: Record<string, { className: string; label: string }> = {
  collectorcrypt: {
    className: "bg-orange-500/15 text-orange-300 border-orange-500/40",
    label: "CC",
  },
  magiceden: {
    className: "bg-purple-500/15 text-purple-300 border-purple-500/40",
    label: "ME",
  },
};

const FALLBACK_VENUE_CLASS = "bg-zinc-700/40 text-zinc-300 border-zinc-600/60";

function shortAddr(addr: string): string {
  if (addr.length <= 10) return addr;
  return `${addr.slice(0, 4)}…${addr.slice(-4)}`;
}

function formatPrice(payload: FeedListing["payload"]): string {
  const native = priceNative(payload);
  const sym = payload.paymentSymbol;
  if (sym === "USDC") {
    return native != null ? usdFmt.format(native) : `${payload.priceRaw} USDC`;
  }
  if (native == null) {
    return `${payload.priceRaw} ${sym ?? shortAddr(payload.paymentMint)}`;
  }
  const label = sym ?? shortAddr(payload.paymentMint);
  return `${tokenFmt.format(native)} ${label}`;
}

interface Props {
  listing: FeedListing;
  density: Density;
}

export function FeedItem({ listing, density }: Props) {
  const { payload, status } = listing;
  const priceLabel = formatPrice(payload);
  // Always link to CollectorCrypt's marketplace page regardless of source
  // venue — it's the canonical card detail page.
  const url = `https://collectorcrypt.com/assets/solana/${payload.mint}`;
  const cardName = payload.metadata.name || payload.mint;
  const s = DENSITY_STYLES[density];
  const venue = VENUE_BADGE[payload.venue];
  const venueClass = venue?.className ?? FALLBACK_VENUE_CLASS;
  const venueLabel = venue?.label ?? payload.venue;

  return (
    <li className="group">
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`flex items-stretch ${s.rowGap} ${s.rowPad} border-b border-zinc-800/70 hover:bg-zinc-800/40 transition-colors`}
      >
        <CardThumbnail
          src={payload.metadata.image}
          alt={cardName}
          thumbSizeClass={s.thumb}
        />

        <div className={`min-w-0 flex-1 flex flex-col justify-center ${s.textGap}`}>
          <div className="flex items-center gap-2 min-w-0">
            <StatusBadge status={status} />
            <span
              className={`px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide rounded border ${venueClass}`}
              title={payload.venue}
            >
              {venueLabel}
            </span>
            <h2 className={`truncate ${s.name} text-zinc-100`}>
              {payload.metadata.name || shortAddr(payload.mint)}
            </h2>
          </div>
          <dl className={`${s.meta} text-zinc-400 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5`}>
            <dt className="text-zinc-500">seller</dt>
            <dd className="font-mono text-zinc-300" title={payload.seller}>
              {shortAddr(payload.seller)}
            </dd>
            <dt className="text-zinc-500">time</dt>
            <dd
              className="font-mono text-zinc-300 tabular-nums"
              title={new Date(listing.receivedAt).toISOString()}
            >
              {timeFmt.format(listing.receivedAt)}
            </dd>
            {status === "sold" && listing.saleBuyer && (
              <>
                <dt className="text-zinc-500">buyer</dt>
                <dd className="font-mono text-zinc-300" title={listing.saleBuyer}>
                  {shortAddr(listing.saleBuyer)}
                </dd>
              </>
            )}
            {status === "cancelled" && listing.cancelInitiator && (
              <>
                <dt className="text-zinc-500">closed by</dt>
                <dd className="text-zinc-300">{listing.cancelInitiator}</dd>
              </>
            )}
          </dl>
        </div>

        <div className="text-right shrink-0 flex flex-col justify-center">
          <div className={`font-mono text-zinc-100 tabular-nums ${s.price}`}>
            {priceLabel}
          </div>
          {payload.metadata.insuredValueUsd != null && (
            <div className={`text-zinc-500 mt-0.5 ${s.insured}`}>
              insured {usdFmt.format(payload.metadata.insuredValueUsd)}
            </div>
          )}
        </div>
      </a>
    </li>
  );
}
